import { useEffect, useRef, useState } from 'react';
// UNIFIED AUTH: Use unified authentication hook for consistent auth state
import useUser from '../hooks/useUser';
import { useLocation, useNavigate } from 'react-router-dom';
import { pushEvent, ANALYTICS_EVENTS } from '../utils/analytics';
import { usePaymentStatus } from '../contexts/PaymentStatusContext';

function AutoLoginHandler() {
  // UNIFIED AUTH: Get authentication state and API token access from unified hook
  const { isAuthenticated, getApiToken } = useUser();
  const { refreshPaymentStatus } = usePaymentStatus();
  const location = useLocation();
  const navigate = useNavigate();
  const loginAttempted = useRef(false);
  const [isVerifying, setIsVerifying] = useState(false);

  const verifySession = async (sessionId) => {
    try {
      console.debug('[AutoLoginHandler] Verifying session:', sessionId);
      
      // UNIFIED AUTH: Get fresh API token before making the request
      const token = await getApiToken();
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/stripe/verify-checkout-session?session_id=${sessionId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to verify session');
      }
      
      const data = await response.json();
      console.debug('[AutoLoginHandler] Verification response:', data);
      
      if (data.status === 'complete') {
        const purchaseValue = Number(data.value ?? data.amount);
        const hasPurchaseValue = Number.isFinite(purchaseValue) && purchaseValue > 0;
        const transactionId = data.transactionId || data.paymentIntentId || sessionId;
        const planType = data.planType || null;

        pushEvent({
          event: ANALYTICS_EVENTS.CHECKOUT_COMPLETE,
          userId: data.userId,
          sessionId: sessionId,
          plan: planType || 'starter',
          amount: data.amount,
          currency: data.currency || 'USD',
          source: location.pathname
        });

        if (hasPurchaseValue) {
          pushEvent({
            event: ANALYTICS_EVENTS.PAYMENT_SUCCESS,
            userId: data.userId,
            sessionId: sessionId,
            transaction_id: transactionId,
            value: purchaseValue,
            currency: data.currency || 'USD',
            plan_type: planType || 'starter',
            products: data.products,
            source: location.pathname,
            event_origin: 'frontend_checkout_verification'
          });
        }

        console.debug('[AutoLoginHandler] Purchase complete, determining redirect based on purchase type');
        
        // Build flow-aware redirect URL
        const params = new URLSearchParams(location.search);
        const redirectPlanType = planType || params.get('plan_type') || 'starter';
        
        // Create base redirect parameters
        const redirectParams = new URLSearchParams();
        redirectParams.set('email', data.userEmail);
        redirectParams.set('source_flow', 'autologin_success');
        redirectParams.set('session_verified', 'true');
        
        // Add customer and payment info if available
        if (data.customerId) redirectParams.set('customerId', data.customerId);
        if (data.paymentMethodId) redirectParams.set('paymentMethodId', data.paymentMethodId);
        if (data.last4) redirectParams.set('last4', data.last4);
        
        let redirectUrl;
        
        if (redirectPlanType === 'starter' || redirectPlanType === 'starter-plan') {
          // ✅ FIXED FLOW: Starter Plan purchases go to upgrade-to-pro with complete context
          redirectParams.set('from_starter_signup', 'true');
          redirectParams.set('plan_type', redirectPlanType);
          redirectUrl = `/upgrade-to-pro?${redirectParams.toString()}`;
          console.debug('[AutoLoginHandler] 🔄 Starter purchase → Upgrade to Pro page');
        } else {
          // Pro/Bootcamp purchases go to survey with purchase context
          redirectParams.set('purchase_success', 'true');
          redirectParams.set('plan_type', redirectPlanType);
          redirectUrl = `/signup-survey?${redirectParams.toString()}`;
          console.debug('[AutoLoginHandler] ✅ Pro/Bootcamp purchase → Signup Survey');
        }
        
        console.debug('[AutoLoginHandler] 🚀 Redirecting to:', redirectUrl);
        try {
          // CRITICAL: Force refresh to bypass backend cache after purchase
          refreshPaymentStatus({ forceRefresh: true });
        } catch (refreshError) {
          console.warn('[AutoLoginHandler] Failed to refresh payment status after session verification:', refreshError);
        }
        navigate(redirectUrl, { replace: true });
        return true;
      } 
      
      if (data.status === 'processing') {
        pushEvent({
          event: ANALYTICS_EVENTS.CHECKOUT_PROCESSING,
          sessionId: sessionId,
          source: location.pathname
        });
        console.debug('[AutoLoginHandler] Still processing, retrying in 2s...');
        await new Promise(resolve => setTimeout(resolve, 2000));
        return verifySession(sessionId);
      }
      
      console.error('[AutoLoginHandler] Session verification failed:', data);
      return false;
    } catch (err) {
      pushEvent({
        event: ANALYTICS_EVENTS.CHECKOUT_ERROR,
        sessionId: sessionId,
        error: String(err),
        source: location.pathname
      });
      console.error('[AutoLoginHandler] Verification error:', err);
      return false;
    }
  };

  useEffect(() => {
    // Use one coherent router snapshot. HostedCheckoutReturnPage can navigate
    // after its async verification finishes while this effect still holds the
    // previous location.search. Reading window.location.pathname here mixed
    // new pathname with stale session_id and sent a completed hosted checkout
    // back through the legacy login/Pro-upsell flow.
    if (location.pathname === '/checkout/hosted-return') {
      return;
    }
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    
    console.debug('[AutoLoginHandler] Mount:', { sessionId, isAuthenticated, loginAttempted: loginAttempted.current });
    
    // Prevent duplicate processing across remounts
    if (!sessionId || sessionId === '{CHECKOUT_SESSION_ID}' || window.__autoLoginAttempted) {
      return;
    }
    window.__autoLoginAttempted = true;
    loginAttempted.current = true;
    
    // If user is already authenticated, verify the session
    if (isAuthenticated) {
      verifySession(sessionId);
    } else {
      const returnUrl = `/upgrade-to-pro?session_id=${sessionId}`;
      navigate(`/login?returnTo=${encodeURIComponent(returnUrl)}`, { replace: true });
    }
  }, [location, navigate, isAuthenticated, getApiToken]);

  return null;
}

export default AutoLoginHandler;
